import { DEFAULT_AUTHENTICATED_ROUTE, LOGIN_PAGE } from '@/config/appRoutes';

/**
 * Query parameter used to carry the destination the user should be sent to
 * after a successful login.
 */
export const REDIRECT_TO_PARAM = 'redirect_to';

/** Minimal location shape needed to build a redirect target */
export interface RedirectLocation {
  pathname: string;
  search?: string;
  hash?: string;
}

/**
 * Builds the full relative URL (pathname + search + hash) for a location.
 */
export const buildRedirectTo = (location: RedirectLocation): string =>
  `${location.pathname}${location.search ?? ''}${location.hash ?? ''}`;

/**
 * Validates a redirect target to prevent open redirect attacks.
 *
 * Only internal, relative URLs are allowed. Absolute URLs, protocol-relative
 * URLs (`//evil.com`), backslash tricks and the login page itself are rejected.
 *
 * @param value - Raw (already URL-decoded) redirect value
 * @returns The safe relative URL, or null when the value is not usable
 */
export const sanitizeRedirectTo = (
  value: string | null | undefined
): string | null => {
  if (!value) return null;

  const trimmedValue = value.trim();
  if (!trimmedValue || trimmedValue !== value) {
    return null;
  }

  if (
    !trimmedValue.startsWith('/') ||
    trimmedValue.startsWith('//') ||
    trimmedValue.includes('\\')
  ) {
    return null;
  }

  const pathname = trimmedValue.split(/[?#]/)[0];
  if (pathname === LOGIN_PAGE) return null;

  return trimmedValue;
};

/**
 * Resolves where an authenticated user should land, based on the
 * `redirect_to` query parameter of the login page.
 *
 * @param search - The login page query string (e.g. `?redirect_to=%2Fkyc`)
 * @returns The safe redirect target or the default authenticated route
 */
export const getRedirectTarget = (search: string | URLSearchParams): string => {
  const params =
    typeof search === 'string' ? new URLSearchParams(search) : search;

  return (
    sanitizeRedirectTo(params.get(REDIRECT_TO_PARAM)) ??
    DEFAULT_AUTHENTICATED_ROUTE
  );
};

/**
 * Builds the login URL preserving the intended destination.
 *
 * @param location - The location the user tried to access
 * @returns `/login` or `/login?redirect_to=<encoded destination>`
 */
export const buildLoginUrlWithRedirect = (
  location: RedirectLocation
): string => {
  const target = sanitizeRedirectTo(buildRedirectTo(location));

  return target
    ? `${LOGIN_PAGE}?${REDIRECT_TO_PARAM}=${encodeURIComponent(target)}`
    : LOGIN_PAGE;
};

/**
 * Navigation state used when the user explicitly logs out, so no destination
 * is remembered for the next login.
 */
export const LOGOUT_NAVIGATION_STATE = { loggedOut: true } as const;

/**
 * Checks whether a navigation state comes from an explicit logout.
 */
export const isLogoutNavigationState = (state: unknown): boolean =>
  typeof state === 'object' &&
  state !== null &&
  (state as { loggedOut?: unknown }).loggedOut === true;
